import React, { useState, useRef, useEffect } from "react"
import { parseSSEStream, parseSSEFinal } from "../../utils/sse"
import StreamingMessage from "./StreamingMessage"
import SuggestedQuestions from "./SuggestedQuestions"
import { DATASETS, COLORS } from "./constants"

const BASE_API = (process.env.REACT_APP_BASE_API_URL || "").replace(/\/+$/, "")

const DATASET_LABELS = {
  [DATASETS.CSRD]: { title: "CSRD Intelligence", subtitle: "AI-powered CSRD benchmarking", placeholder: "Ask anything about CSRD reports..." },
  [DATASETS.NIFTY]: { title: "BRSR Intelligence", subtitle: "AI-powered Indian ESG benchmarking", placeholder: "Ask anything about BRSR / Nifty ESG..." },
  [DATASETS.ALL]: { title: "ESG Intelligence", subtitle: "AI-powered cross-market benchmarking", placeholder: "Ask anything about ESG reports..." },
}

const MAX_MESSAGES = 100

const AskAnythingChat = ({ companyContext, selectedYear, dataset = DATASETS.CSRD }) => {
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState("")
  const [isStreaming, setIsStreaming] = useState(false)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)
  const abortRef = useRef(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])

  useEffect(() => {
    if (abortRef.current) abortRef.current.abort()
    setMessages([])
    setInput("")
    setIsStreaming(false)
  }, [dataset])

  // SSE parsing lives in utils/sse.js (tested): only complete events are
  // emitted; the unterminated tail stays in the buffer. The old inline parser
  // emitted the growing partial on every read, duplicating the answer.

  const askQuestion = async (query) => {
    if (!query.trim() || isStreaming) return

    const userMsg = { role: "user", content: query.trim() }
    const assistantMsg = {
      role: "assistant",
      content: "",
      classification: null,
      sources: [],
      isStreaming: true,
    }

    setMessages((prev) => {
      const next = [...prev, userMsg, assistantMsg]
      return next.length > MAX_MESSAGES ? next.slice(-MAX_MESSAGES) : next
    })
    setInput("")
    setIsStreaming(true)

    if (abortRef.current) abortRef.current.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl

    try {
      const response = await fetch(`${BASE_API}/csrd-benchmark-ask-stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: query.trim(),
          dataset,
          company_context: companyContext || undefined,
          year: selectedYear || undefined,
        }),
        signal: ctrl.signal,
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ""

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const { events, remainder } = parseSSEStream(buffer)
        buffer = remainder

        for (const evt of events) {
          setMessages((prev) => {
            const updated = [...prev]
            const last = { ...updated[updated.length - 1] }

            switch (evt.event) {
              case "classification":
                try {
                  last.classification = JSON.parse(evt.data)
                } catch (e) {
                  // ignore parse errors
                }
                break
              case "chunk":
                last.content += evt.data
                break
              case "sources":
                try {
                  last.sources = JSON.parse(evt.data)
                } catch (e) {
                  // ignore parse errors
                }
                break
              case "done":
                last.isStreaming = false
                break
              default:
                break
            }

            updated[updated.length - 1] = last
            return updated
          })
        }
      }

      // A final event may arrive without its terminator; apply it once.
      for (const evt of parseSSEFinal(buffer)) {
        setMessages((prev) => {
          const updated = [...prev]
          const last = { ...updated[updated.length - 1] }
          if (evt.event === "chunk") last.content += evt.data
          if (evt.event === "done") last.isStreaming = false
          updated[updated.length - 1] = last
          return updated
        })
      }

      // Ensure streaming flag is cleared
      setMessages((prev) => {
        const updated = [...prev]
        const last = { ...updated[updated.length - 1] }
        last.isStreaming = false
        updated[updated.length - 1] = last
        return updated
      })
    } catch (error) {
      if (error.name === "AbortError") return
      console.error("Ask Anything error:", error)
      setMessages((prev) => {
        const updated = [...prev]
        const last = { ...updated[updated.length - 1] }
        last.content = "Sorry, I couldn't process your question. Please try again."
        last.isStreaming = false
        updated[updated.length - 1] = last
        return updated
      })
    } finally {
      setIsStreaming(false)
      inputRef.current?.focus()
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    askQuestion(input)
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      askQuestion(input)
    }
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "#fff",
        borderRight: "1px solid #E5E5E5",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "16px 20px",
          borderBottom: "1px solid #E5E5E5",
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#407775" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        <div>
          <h3 style={{ margin: 0, fontSize: "15px", fontWeight: "600", color: "#494949" }}>
            Ask Anything
          </h3>
          <p style={{ margin: 0, fontSize: "11px", color: "#707070" }}>
            {DATASET_LABELS[dataset]?.subtitle || "AI-powered ESG intelligence"}
          </p>
        </div>
        {companyContext && (
          <span
            style={{
              marginLeft: "auto",
              fontSize: "11px",
              color: "#407775",
              backgroundColor: "#DAECEC",
              padding: "2px 8px",
              borderRadius: "10px",
            }}
          >
            {companyContext}
          </span>
        )}
      </div>

      {/* Messages area */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "20px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {messages.length === 0 && (
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              padding: "20px",
            }}
          >
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#407775"
              strokeWidth="1.5"
              style={{ marginBottom: "16px", opacity: 0.5 }}
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <h4 style={{ color: "#494949", margin: "0 0 4px 0", fontSize: "16px" }}>
              {DATASET_LABELS[dataset]?.title || "ESG Intelligence"}
            </h4>
            <p style={{ color: "#707070", fontSize: "12px", margin: "0 0 20px 0", maxWidth: "280px" }}>
              {dataset === DATASETS.NIFTY
                ? "Ask questions about BRSR disclosures, emissions, and frameworks for Nifty companies"
                : dataset === DATASETS.ALL
                  ? "Ask questions across European CSRD and Indian BRSR datasets"
                  : "Ask questions about CSRD reports, emissions data, ESG disclosures, and peer comparisons"}
            </p>
            <SuggestedQuestions onSelect={askQuestion} />
          </div>
        )}

        {messages.map((msg, i) => (
          <StreamingMessage
            key={i}
            message={msg}
            onFollowUp={
              msg.role === "assistant" && !msg.isStreaming && i === messages.length - 1
                ? askQuestion
                : null
            }
          />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <form
        onSubmit={handleSubmit}
        style={{
          padding: "12px 16px",
          borderTop: "1px solid #E5E5E5",
          backgroundColor: "#FAFAFA",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            backgroundColor: "#fff",
            borderRadius: "24px",
            border: "1px solid #E5E5E5",
            padding: "4px 4px 4px 16px",
            transition: "border-color 0.15s",
          }}
        >
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={DATASET_LABELS[dataset]?.placeholder || "Ask anything about ESG reports..."}
            disabled={isStreaming}
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              fontSize: "13px",
              color: "#494949",
              backgroundColor: "transparent",
            }}
          />
          <button
            type="submit"
            disabled={!input.trim() || isStreaming}
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              border: "none",
              backgroundColor: input.trim() && !isStreaming ? "#407775" : "#E5E5E5",
              color: "#fff",
              cursor: input.trim() && !isStreaming ? "pointer" : "default",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background-color 0.15s",
              flexShrink: 0,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      </form>
    </div>
  )
}

export default AskAnythingChat
